import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ToetsFields from './ToetsFields';
import StudentChoice from './StudentChoice';
import ToetsChoice from './ToetsChoice';
import { api, emptyPart, emptyResult, errorMessage, resultPayload } from './toetsUtils';
import './Toetsen.css';

export default function ToetsForm({ readOnly = false }) {
  const { id } = useParams();
  const [query] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => ({ ...emptyResult(), student_id: query.get('student_id') || '' }));
  const [studentName, setStudentName] = useState(query.get('student_name') || '');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formKey, setFormKey] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const submitting = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setLoadFailed(false); setError('');
    Promise.all([
      api('get', '/templates', undefined, { signal: controller.signal }),
      id ? api('get', `/${id}`, undefined, { signal: controller.signal }) : Promise.resolve(null)
    ]).then(([standard, result]) => {
      setTemplates(standard.data.data);
      if (result) {
        const data = result.data.data;
        setForm({ ...data, parts: data.parts.map(p => ({ ...p, correct_count: p.correct_count ?? '', incorrect_count: p.incorrect_count ?? '', percentage: p.percentage ?? '', grade: p.grade ?? '' })) });
        setStudentName(data.student_name);
      } else {
        setForm({ ...emptyResult(), student_id: query.get('student_id') || '' });
        setStudentName(query.get('student_name') || '');
      }
    }).catch(err => { if (!axios.isCancel(err)) { setError(errorMessage(err)); setLoadFailed(true); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [id, query]);
  const changeTitle = title => setForm({ ...form, title, template_id: title === form.title ? form.template_id : '' });
  const applyChoice = source => {
    const filled = v => v !== '' && v !== null && v !== undefined;
    if ((form.parts.length || [form.correct_count, form.incorrect_count, form.grade].some(filled)) &&
      !window.confirm('Dit vervangt de titel en onderdelen en wist de ingevoerde scores. Leerling, schooljaar en datum blijven behouden. Doorgaan?')) return false;
    setForm({ ...form, title: source.title, template_id: source.template_id,
      correct_count: '', incorrect_count: '', grade: '',
      parts: source.parts.map(p => ({ ...emptyPart(), title: p.title })) });
    return true;
  };
  const save = async e => {
    e.preventDefault();
    if (submitting.current || choosing) return;
    const addAnother = !id && e.nativeEvent.submitter?.value === 'add-another';
    submitting.current = true; setSaving(true); setError(''); setSuccess('');
    try {
      const payload = resultPayload(form);
      await api(id ? 'put' : 'post', id ? `/${id}` : '', payload);
      if (addAnother) {
        setForm({ ...emptyResult(), title: form.title, template_id: form.template_id,
          parts: form.parts.map(part => ({ ...emptyPart(), title: part.title })) });
        setStudentName('');
        setFormKey(key => key + 1);
        setSuccess('Toets opgeslagen. Kies een leerling en voer het volgende resultaat in.');
        return;
      }
      navigate(`/toetsen?student_id=${form.student_id}&student_name=${encodeURIComponent(studentName)}&school_year=${encodeURIComponent(form.school_year || 'none')}`, { state: { message: id ? 'Toets bijgewerkt.' : 'Toets opgeslagen.' } });
    } catch (err) { setError(errorMessage(err)); }
    finally { submitting.current = false; setSaving(false); }
  };
  return <div className="toetsen-page">
    <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start mb-4">
      <div><h2>{readOnly ? 'Toetsresultaat' : id ? 'Toets bewerken' : 'Nieuw toetsresultaat'}</h2>
        <p className="text-muted mb-0">{readOnly ? 'Resultaat en onderdelen per leerling.' : 'Kies een leerling. Van de toetsvelden is alleen de titel verplicht.'}</p></div>
      <Link className="btn btn-outline-secondary" to="/toetsen">Terug naar overzicht</Link>
    </div>
    {success && <div className="alert alert-success" role="status">{success}</div>}
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {loading ? <p role="status">Toetsgegevens laden…</p> : !loadFailed && <form className="toets-panel" onSubmit={save}>
      <fieldset disabled={saving || choosing}>
        <div className="mb-4"><label className="form-label" htmlFor="toets-student">Leerling *</label>
          <StudentChoice key={formKey} value={form.student_id} selectedName={studentName} disabled={Boolean(id) || readOnly} required
            onChange={(student_id, name) => { setForm({ ...form, student_id }); setStudentName(name); }} />
        </div>
        {!readOnly && <ToetsChoice templates={templates} currentId={id} onApply={applyChoice} onBusyChange={setChoosing} />}
        <ToetsFields value={form} onChange={setForm} disabled={readOnly} titleChange={changeTitle} />
        {readOnly ? <Link className="btn btn-primary mt-3" to={`/toetsen/${id}/bewerken`}>Bewerken</Link>
          : <div className="d-flex flex-wrap gap-2 mt-4"><button className="btn btn-primary" type="submit" disabled={saving || choosing}>{saving ? 'Opslaan…' : 'Opslaan'}</button>
            {!id && <button className="btn btn-outline-primary" type="submit" value="add-another" disabled={saving || choosing}>Opslaan en nog een resultaat toevoegen</button>}
            <Link className="btn btn-outline-secondary" to="/toetsen">Annuleren</Link></div>}
      </fieldset>
    </form>}
  </div>;
}
